import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Github, Target, Lightbulb, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import delThumbnail from "@/assets/del-thumbnail.jpg";

const Projects = () => {
  const projects = [
    {
      name: "VAC (Venta Asistida al Cliente)",
      summary: "Modular internal sales platform using microfrontend architecture",
      problem: "Claro needed an internal SaaS platform to manage stock and handle assisted in-store sales with multiple payment methods, discount rules, and interest management.",
      solution: "Built a microfrontend architecture to decouple key business modules — sales, payments, stock, and discount management. Created reusable UI components and optimized global state management.",
      outcome: "Reduced load times by 35%, decreased transaction errors by 25%, and reduced maintenance effort by 40%.",
      role: "Frontend Developer & Architect — designed modular structure, implemented state management, maintained code quality through testing.",
      technologies: ["React", "TypeScript", "Zustand", "Jest", "React Testing Library", "NGINX", "Jenkins"],
      liveDemo: "#",
      sourceCode: "#",
      caseStudy: "/case-studies/vac",
      image: new URL("@/assets/vac-thumbnail.jpg", import.meta.url).href,
    },
    {
      name: "DEL-Frontend",
      summary: "Internal web application for managing pickup and delivery orders",
      problem: "Claro required an internal application to efficiently manage pickup and delivery orders, handling claims, cancellations, and order status updates for smooth logistics coordination.",
      solution: "Designed a clean MVC-inspired project structure using React and Vite. Configured NGINX for multi-environment deployment and optimized performance using Google Lighthouse insights.",
      outcome: "Page load time improved by 32%, app responsiveness increased by 28%, and QA teams reported 40% reduction in front-end-related issues.",
      role: "Frontend Developer — responsible for all frontend aspects from structure to deployment, collaborated with backend and QA teams.",
      technologies: ["React", "Vite", "TypeScript", "Zustand", "SCSS", "Jest", "NGINX", "Jenkins"],
      liveDemo: "#",
      sourceCode: "#",
      caseStudy: "/case-studies/del-frontend",
      image: delThumbnail,
    },
    {
      name: "DineUp",
      summary: "POS SaaS for restaurants using Node.js + React",
      problem: "Restaurants needed an affordable, real-time POS system that could handle orders, inventory, and analytics without expensive hardware.",
      solution: "Built a cloud-based SaaS platform with React frontend and Node.js backend, featuring live order tracking, inventory management, and a comprehensive analytics dashboard.",
      outcome: "Reduced order processing time by 40% and enabled real-time inventory tracking for 15+ restaurant clients.",
      role: "Full Stack Developer — designed architecture, built REST APIs, implemented real-time features with WebSockets.",
      technologies: ["Node.js", "React", "PostgreSQL", "Express", "WebSockets"],
      liveDemo: "#",
      sourceCode: "https://github.com/lucasfer01",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop",
    },
    {
      name: "Delivery Management Platform",
      summary: "Enterprise microfrontend system for logistics at Claro",
      problem: "Claro Argentina needed a scalable platform to coordinate thousands of daily delivery operations across multiple teams and systems.",
      solution: "Developed a microfrontend architecture using React, Zustand for state management, and NGINX for routing. Integrated with legacy systems while maintaining independent deployment cycles.",
      outcome: "Successfully scaled to handle 5,000+ daily operations with 30% faster load times through code splitting and optimized state management.",
      role: "Frontend Developer — architected microfrontend system, implemented state management patterns, optimized performance.",
      technologies: ["React", "Zustand", "NGINX", "TypeScript", "Module Federation"],
      liveDemo: "#",
      sourceCode: "#",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=400&fit=crop",
    },
    {
      name: "Portfolio Website",
      summary: "Modern, responsive developer portfolio with smooth animations",
      problem: "Needed a professional portfolio that showcases technical skills while demonstrating design sensibility and modern web development practices.",
      solution: "Built with React, TypeScript, and Framer Motion for smooth animations. Implemented responsive design with TailwindCSS and optimized for performance and SEO.",
      outcome: "Achieved 95+ Lighthouse score across all metrics with seamless animations and mobile-first responsive design.",
      role: "Solo Developer — full design and development, UX optimization, SEO implementation.",
      technologies: ["React", "TypeScript", "Framer Motion", "TailwindCSS", "Vite"],
      liveDemo: "#",
      sourceCode: "https://github.com/lucasfer01",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop",
    },
  ];

  return (
    <section id="projects" className="py-24">
      <div className="container mx-auto px-6">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold mb-4 text-center"
        >
          Featured <span className="text-gradient">Projects</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto"
        >
          Real-world solutions with measurable impact
        </motion.p>

        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="bg-card border-border hover:border-primary transition-all duration-300 card-shadow overflow-hidden group h-full flex flex-col">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent opacity-60" />
                </div>
                
                <CardHeader>
                  <CardTitle className="text-2xl">{project.name}</CardTitle>
                  <CardDescription className="text-muted-foreground font-mono text-sm">
                    {project.summary}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col">
                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">Problem</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{project.problem}</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">Solution & Role</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-2">{project.solution}</p>
                      <p className="text-xs text-muted-foreground/80 italic">{project.role}</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">Impact</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{project.outcome}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 bg-secondary text-xs font-mono rounded border border-border"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex gap-3 mt-auto">
                    {project.caseStudy ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                        asChild
                      >
                        <a href={project.caseStudy}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Case Study
                        </a>
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                        asChild
                      >
                        <a href={project.liveDemo} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Demo
                        </a>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      asChild
                    >
                      <a href={project.sourceCode} target="_blank" rel="noopener noreferrer">
                        <Github className="w-4 h-4 mr-2" />
                        Code
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
